import EntityFilter from "src/components/common/EntityFilter";
import { Box, CircularProgress } from "@mui/material";
import { AxiosError, AxiosResponse } from "axios";
import { useState } from "react";
import { useQuery } from "react-query";
import {
  createSearchParams,
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import DashboardWrapper from "src/components/common/DashboardWrapper";
import DataGrid from "src/components/common/DataGrid";
import EmptyView from "src/components/common/EmptyView";
import { Button } from "src/components/common/FormComponents/Buttons";
import { getCostEstimateDetails } from "src/utils/api/dashboard";
import { dateAndTimeStamp, downloadSheet } from "src/utils/helper";
import defaultStyles, { StylesClasses } from "./styles";
import { path } from "src/constants/path";
import SearchBar from "src/components/common/SearchField";
import { getStyles } from "src/styles/theme";
import HorizontalTabs from "src/components/common/HorizontalTabs";
import { costEstimateDetails, fundPaymentTabs } from "src/constants/common";
import { useAppSelector } from "src/redux/store";

const UpdateProgressDetails = () => {
  const [tableRows, setTableRows] = useState([]);

  const styles = getStyles<StylesClasses>(defaultStyles);

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  const tab = searchParams.get("currentTab");

  const user = useAppSelector((state) => state.root.user);

  const [queryParams, setQueryParams] = useState<{
    offset: number;
    limit: number;
    download: boolean;
    sortBy: string;
    searchText: string;
    projectStateIds?: string[];
    landOwnershipStatus: string;
    targetStatus: string;
    entityIds?: string;
  }>({
    offset: 0,
    limit: 300,
    download: false,
    sortBy: "updated_at:DESC",
    searchText: "",
    projectStateIds: undefined,
    landOwnershipStatus: "APPROVED",
    targetStatus: "SUBMIT_TO_SNO",
  });

  const { isLoading, isFetching } = useQuery(
    ["projectGet", queryParams],
    () =>
      getCostEstimateDetails({
        ...queryParams,
      }),
    {
      onSuccess: (res: AxiosResponse) => {
        const row = res?.data?.data?.rows.map((row) => ({
          sno: row?.serialNo,
          id: row?.id,
          costEstimateID: row?.id,
          registrationNo: row?.projectDetail.registration_no,
          entityName: row?.user.firstName,
          state: row?.projectDetail?.address?.stateDetail?.name,
          division: row?.projectDetail.landSpecification.division,
          totalArea: `${row?.netPlantationArea}`,
          ecoRestorationAmount: row?.demandNote ?? "-",
          created_at: dateAndTimeStamp(row?.created_at, "IST"),
          updated_at: dateAndTimeStamp(row?.updated_at, "IST"),
          totalFinancialProgress: row.costProgress
            ? Number(row.costProgress).toFixed(2)
            : "0.00",
          totalSeedlings: row?.totalNumberOfSeedlingToBePlanted,
          isDigitalCostEstimateFilled: row?.isDigitalCostEstimateFilled,
          isProgressFilled: row?.isProgressFilled,
          pendingQueryCount: row?.pendingQueryCount || 0,
          pendingSeedlingQueryCount: row?.pendingSeedlingQueryCount || 0,
        }));

        if (queryParams.download) {
          const exportData = row.map((item) => {
            return {
              "S.No": item?.sno,
              "Cost Estimate ID": item?.costEstimateID,
              "Registration No": item?.registrationNo,
              "Entity Name": item.entityName,
              State: item?.state,
              Division: item?.division,
              "Net Planned Area (Ha)": item?.totalArea,
              "Pending Progress Queries": item?.pendingQueryCount,
              "Pending Seedling Queries": item?.pendingSeedlingQueryCount,
              "Eco-Restoration Cost (₹)": item?.ecoRestorationAmount,
              "Total Financial Progress (₹)": item?.totalFinancialProgress,
              "Total No. of Seedlings Proposed": item?.totalSeedlings,
              "Digital Cost Estimate Filled": item?.isDigitalCostEstimateFilled
                ? "Yes"
                : "No",
              "Progress Filled": item?.isProgressFilled ? "Yes" : "No",
              "Created At": item?.created_at,
              "Updated At": item?.updated_at,
            };
          });
          downloadSheet(exportData);
          setQueryParams((prev) => ({
            ...prev,
            download: false,
          }));
        } else {
          setTableRows(row);
        }
      },
      onError: (err: AxiosError) => {},
      refetchOnMount: true,
    }
  );

  const RenderEPID = (params) => {
    return (
      <>
        <Box
          component={RouterLink}
          to={`${path.advanceWorkProgress}?${createSearchParams({
            id: params?.row?.costEstimateID,
          })}`}
          sx={{ ...defaultStyles.renderButtonText2 }}
        >{`${params?.row?.costEstimateID}`}</Box>
      </>
    );
  };

  const tableColumns: any = [
    {
      field: "sno",
      headerName: "S.No",
      width: 90,
      sortable: false,
    },
    {
      field: "costEstimateID",
      headerName: "Cost Estimate ID",
      width: 220,
      sortable: false,
      renderCell: RenderEPID,
    },
    {
      field: "registrationNo",
      headerName: "Reg.No",
      width: 100,
      sortable: false,
    },
    {
      field: "entityName",
      headerName: "Entity Name",
      width: 150,
      sortable: true,
    },
    {
      field: "state",
      headerName: "State",
      width: 150,
      sortable: true,
    },
    {
      field: "division",
      headerName: "Division",
      width: 150,
      sortable: true,
      userType: ["INDIVIDUAL"],
      tab: [],
    },
    {
      field: "totalArea",
      headerName: "Net Planned Area(Ha)",
      width: 150,
      sortable: false,
    },
    {
      field: "pendingQueryCount",
      headerName: "Pending Progress Queries",
      width: 175,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {params.row.pendingQueryCount > 0 ? (
            <Box
              sx={{
                backgroundColor: "#ff5252",
                color: "white",
                borderRadius: "12px",
                padding: "4px 12px",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              {params.row.pendingQueryCount}
            </Box>
          ) : (
            <Box sx={{ color: "#999", fontSize: "14px" }}>0</Box>
          )}
        </Box>
      ),
    },
    {
      field: "pendingSeedlingQueryCount",
      headerName: "Pending Seedling Queries",
      width: 175,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {params.row.pendingSeedlingQueryCount > 0 ? (
            <Box
              sx={{
                backgroundColor: "#ff5252",
                color: "white",
                borderRadius: "12px",
                padding: "4px 12px",
                fontWeight: "bold",
                fontSize: "14px",
              }}
            >
              {params.row.pendingSeedlingQueryCount}
            </Box>
          ) : (
            <Box sx={{ color: "#999", fontSize: "14px" }}>0</Box>
          )}
        </Box>
      ),
    },
    {
      field: "ecoRestorationAmount",
      headerName: "Eco-Restoration Cost(₹)",
      width: 200,
      sortable: false,
    },
    {
      field: "totalFinancialProgress",
      headerName: "Total Financial Progress(₹)",
      width: 220,
      sortable: false,
    },

    {
      field: "created_at",
      headerName: "Created At",
      width: 150,
      sortable: true,
    },
    {
      field: "updated_at",
      headerName: "UPDATED AT",
      width: 150,
      sortable: true,
    },
  ];

  const exportToExcelEntity = () => {
    setQueryParams({
      ...queryParams,
      download: true,
    });
  };

  const handleSort = (sortByField) => {
    setQueryParams({
      ...queryParams,
      sortBy: `${sortByField[0]?.field}:${sortByField[0]?.sort.toUpperCase()}`,
    });
  };

  return (
    <DashboardWrapper
      title={`${
        user?.data?.userType === null
          ? "Update Progress Details"
          : "Progress Details"
      }`}
      buttonIcon="/assets/svg/roundPlusIcon.svg"
      buttonText=""
      onButtonClick={() => {}}
      showBtn={!!tableRows.length}
      showBackBtn={true}
    >
      <Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: "2%",
            padding: "10px 1%",
            background: "white",
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
          }}
        >
          <SearchBar
            getText={(e) => {
              setQueryParams((a) => {
                return { ...queryParams, searchText: `${e}`, offset: 0 };
              });
            }}
            customStyles={{
              wrapper: {
                flex: 1,
                minWidth: "240px",
              },
            }}
          />
          <EntityFilter
            source="progress"
            filters={{
              landOwnershipStatus: queryParams.landOwnershipStatus,
              targetStatus: queryParams.targetStatus,
              searchText: queryParams.searchText,
            }}
            value={queryParams.entityIds?.split(",") ?? []}
            onChange={(ids) => {
              setQueryParams((prev) => ({
                ...prev,
                entityIds: ids.length ? ids.join(",") : undefined,
                offset: 0,
              }));
            }}
          />
          <Button onClick={exportToExcelEntity} text={"Export"} />
        </Box>
        {/* Data Grid */}
        {isLoading || isFetching ? (
          <Box {...styles("flexContainer")}>
            <CircularProgress />
          </Box>
        ) : tableRows.length > 0 ? (
          <DataGrid
            rows={tableRows}
            columns={tableColumns}
            rowCount={tableRows.length}
            rowsPerPageOptions={[10]}
            sortingMode="server"
            onSortModelChange={handleSort}
            paginationMode="client"
            pageSize={50}
            columnBuffer={20}
            loading={isLoading}
            getRowId={(row) => `${row.id}-${row.userName}`}
          />
        ) : (
          <EmptyView
            heading="No Entry Found"
            subheading=""
            onClick={() => {}}
            buttonText={""}
          />
        )}
      </Box>
    </DashboardWrapper>
  );
};

export default UpdateProgressDetails;
